/**
 * The countries a phone number can belong to, with the digits that open a number from there (D-323).
 *
 * `dial` is the country calling code (ITU E.164) — `91` for India — and, for the members of the North American
 * numbering plan (calling code 1) that are separate countries, the code with their area code: Jamaica is `1876`, so
 * `+1 876 …`. The names are not here: a browser knows them in every language (`Intl.DisplayNames`, region).
 *
 * Order: India first (the firm is in India, the form's default), then by English name; the picker sorts by name in
 * the visitor's language anyway.
 */
export interface Country {
  /** ISO 3166-1 alpha-2 (`XK`, Kosovo, is the code in common use for it). */
  iso: string;
  /** Digits only, no `+`: the calling code, with the area code for NANP countries other than the US and Canada. */
  dial: string;
}

const ROWS =
  'IN91 AF93 AL355 DZ213 AS1684 AD376 AO244 AI1264 AG1268 AR54 AM374 AW297 AU61 AT43 AZ994 BS1242 BH973 BD880 ' +
  'BB1246 BY375 BE32 BZ501 BJ229 BM1441 BT975 BO591 BQ599 BA387 BW267 BR55 IO246 VG1284 BN673 BG359 BF226 BI257 ' +
  'KH855 CM237 CA1 CV238 KY1345 CF236 TD235 CL56 CN86 CX61 CC61 CO57 KM269 CG242 CD243 CK682 CR506 CI225 HR385 ' +
  'CU53 CW599 CY357 CZ420 DK45 DJ253 DM1767 DO1809 EC593 EG20 SV503 GQ240 ER291 EE372 SZ268 ET251 FK500 FO298 ' +
  'FJ679 FI358 FR33 GF594 PF689 GA241 GM220 GE995 DE49 GH233 GI350 GR30 GL299 GD1473 GP590 GU1671 GT502 GG44 ' +
  'GN224 GW245 GY592 HT509 HN504 HK852 HU36 IS354 ID62 IR98 IQ964 IE353 IM44 IL972 IT39 JM1876 JP81 JE44 JO962 ' +
  'KZ7 KE254 KI686 XK383 KW965 KG996 LA856 LV371 LB961 LS266 LR231 LY218 LI423 LT370 LU352 MO853 MG261 MW265 ' +
  'MY60 MV960 ML223 MT356 MH692 MQ596 MR222 MU230 YT262 MX52 FM691 MD373 MC377 MN976 ME382 MS1664 MA212 MZ258 ' +
  'MM95 NA264 NR674 NP977 NL31 NC687 NZ64 NI505 NE227 NG234 NU683 NF672 KP850 MK389 MP1670 NO47 OM968 PK92 ' +
  'PW680 PS970 PA507 PG675 PY595 PE51 PH63 PL48 PT351 PR1787 QA974 RE262 RO40 RU7 RW250 BL590 SH290 KN1869 ' +
  'LC1758 MF590 PM508 VC1784 WS685 SM378 ST239 SA966 SN221 RS381 SC248 SL232 SG65 SX1721 SK421 SI386 SB677 ' +
  'SO252 ZA27 KR82 SS211 ES34 LK94 SD249 SR597 SE46 CH41 SY963 TW886 TJ992 TZ255 TH66 TL670 TG228 TK690 TO676 ' +
  'TT1868 TN216 TR90 TM993 TC1649 TV688 UG256 UA380 AE971 GB44 US1 UY598 VI1340 UZ998 VU678 VA39 VE58 VN84 ' +
  'WF681 YE967 ZM260 ZW263';

export const COUNTRIES: readonly Country[] = ROWS.split(' ').map((row) => ({
  iso: row.slice(0, 2),
  dial: row.slice(2),
}));

/** The country the form starts with. */
export const DEFAULT_COUNTRY = 'IN';

const BY_ISO = new Map(COUNTRIES.map((country) => [country.iso, country]));

export function countryByIso(iso: string): Country | undefined {
  return BY_ISO.get(iso);
}

/** The country calling code of a `dial` (`1876` → `1`, `91` → `91`). */
export const callingCode = (dial: string): string => (dial.startsWith('1') ? '1' : dial);

/** Every country calling code there is (`1`, `7`, `20`, `91` …). They never start one another, so a number has one. */
export const CALLING_CODES: ReadonlySet<string> = new Set(
  COUNTRIES.map((country) => callingCode(country.dial)),
);

/** Of the countries that share a calling code, the one a number with just that code is taken for. */
const SHARED_CODE_DEFAULT: Readonly<Record<string, string>> = {
  '1': 'US',
  '7': 'RU',
  '39': 'IT',
  '44': 'GB',
  '47': 'NO',
  '61': 'AU',
  '212': 'MA',
  '262': 'RE',
  '358': 'FI',
  '590': 'GP',
  '599': 'CW',
};

/** The country a number with this calling code is taken for (`1` → United States, `44` → United Kingdom …). */
export function countryForCallingCode(code: string): Country | undefined {
  const preferred = SHARED_CODE_DEFAULT[code];
  if (preferred) return BY_ISO.get(preferred);
  return COUNTRIES.find((country) => callingCode(country.dial) === code);
}
